import { Field, FieldValue } from './field/field';

export class LayoutGroup {
	constructor(
		readonly title: string,
		readonly sections: LayoutSection[]
	) {}
}

export class LayoutSection {
	constructor(
		readonly title: string | undefined,
		readonly fields: Field<FieldValue>[]
	) {}
}

export interface LayoutProductCard {
	groupsLeft: LayoutGroup[];
	groupsLeftLeft: LayoutGroup[];
	groupsLeftRight: LayoutGroup[];
	groupsRight: LayoutGroup[];
}

export interface LayoutGroupContent {
	title: string;
	sections: LayoutSectionContent[];
}

export interface LayoutSectionContent {
	title?: string;
	characteristics: string[];
}

const GROUP_SPECIFICATIONS: LayoutGroupContent = {
	title: 'product.title.group.specifications',
	sections: [
		{
			characteristics: [
				'soldToParty',
				'shipToParty',
				'endUser',
				'steelGrade',
				'usePartReference',
				'skinPassProcessCode',
				'floorPlatesPattern',
				'floorPlatesPatternSide',
				'normalising'
			]
		}
	]
};

const GROUP_DIMENSIONS: LayoutGroupContent = {
	title: 'product.title.group.dimensions',
	sections: [
		{
			characteristics: ['unitOfDimensions']
		},
		{
			title: 'product.title.section.thickness',
			characteristics: [
				'thicknessValue',
				'thicknessToleranceMinus',
				'thicknessTolerancePlus',
				'aimedThickness',
				'thicknessTolerance'
			]
		},
		{
			title: 'product.title.section.width',
			characteristics: [
				'widthValue',
				'widthToleranceMinus',
				'widthTolerancePlus',
				'widthValueUS',
				'widthTolerance'
			]
		},
		{
			title: 'product.title.section.length',
			characteristics: [
				'lengthValue',
				'lengthToleranceMinus',
				'lengthTolerancePlus',
				'lengthValueUS',
				'lengthTolerance',
			]
		},
		{
			title: 'product.title.section.edge',
			characteristics: [
				'edgeTrimmedInLastTool',
				'edgeShapeSpecification',
				'edgeSlitMarking'
			]
		},
		{
			characteristics: [
				'flatnessTolerance',
				'transversalProfileTolerance',
				'camberTolerance',
				'squarenessTolerance',
				'referenceCuttingDesign',
				'yieldFactorWeightConversion',
				'profileVariableThickness'
			]
		},
		{
			characteristics: [
				'diskDiameter',
				'diskDiameterUnit'
			]
		}
	]
};

const GROUP_COATING: LayoutGroupContent = {
	title: 'product.title.group.coating',
	sections: [
		{
			characteristics: ['metallicCoating']
		},
		{
			title: 'product.title.section.top',
			characteristics: [
				'organicDatasheetNameTop',
				'colourTop',
				'nominalThicknessValueTop',
				'surfaceOrganicTextureTop',
				'nominalGlossValueTop'
			]
		},
		{
			title: 'product.title.section.bottom',
			characteristics: [
				'organicDatasheetNameBottom',
				'colourBottom',
				'nominalThicknessValueBottom',
				'surfaceOrganicTextureBottom',
				'nominalGlossValueBottom'
			]
		},
		{
			title: 'product.title.section.removableProtectiveFilm',
			characteristics: [
				'strippableFilmType',
				'strippableFilmSide',
				'strippableFilmFreeEdgeWidthCode'
			]
		},
		{
			characteristics: [
				'embossingCode',
				'spangle',
				'baseMaterial',
				'specialAptitude'
			]
		}
	]
};

const GROUP_WEIGHT_DIAMETER: LayoutGroupContent = {
	title: 'product.title.group.weightDiameter',
	sections: [
		{
			title: 'product.title.section.weight',
			characteristics: ['weightUnit']
		},
		{
			title: 'product.title.section.coilWeight',
			characteristics: [
				'minNetWeightValue',
				'maxNetWeightValue'
			]
		},
		{
			title: 'product.title.section.packageWeight',
			characteristics: [
				'minGrossWeightValue',
				'maxGrossWeightValue'
			]
		},
		{
			title: 'product.title.section.packageHeight',
			characteristics: ['packagingHeightDimensionUnit']
		},
		{
			title: 'product.title.section.packageHeightWithPalet',
			characteristics: [
				'packagingHeightMinimumValue',
				'packagingHeightMaximumValue'
			]
		},
		{
			title: 'product.title.section.numberSlitCoilsPerPackage',
			characteristics: [
				'minNumberOfSlitCoilsPerPackage',
				'maxNumberOfSlitCoilsPerPackage'
			]
		},
		{
			title: 'product.title.section.diameter',
			characteristics: ['outsideDiameterUnit']
		},
		{
			title: 'product.title.section.outsideDiameter',
			characteristics: [
				'minOutsideDiameter',
				'maxOutsideDiameter'
			]
		},
		{
			title: 'product.title.section.insideDiameter',
			characteristics: [
				'insideDiameterMinValue',
				'insideDiameterMaxValue',
				'insideDiameterCode'
			]
		},
		{
			title: 'product.title.section.specificWeight',
			characteristics: [
				'specificWeightUnit',
				'minWeightPerWidthUnitValue',
				'maxWeightPerWidthUnitValue'
			]
		},
		{
			title: 'product.title.section.bundleWeight',
			characteristics: [
				'minBundleWeightValue',
				'maxBundleWeightValue'
			]
		},
		{
			title: 'product.title.section.bundleHeight',
			characteristics: [
				'unitOfBundleHeight',
				'minBundleHeight',
				'maxBundleHeight',
				'bundleHeightIncludePackaging'
			]
		},
		{
			title: 'product.title.section.numberSheetsPerBundle',
			characteristics: [
				'minNumberOfSheetPerBundle',
				'maxNumberOfSheetPerBundle',
				'numberOfSheetPerBundle'
			]
		},
		{
			title: 'product.title.section.slabWeightInTon',
			characteristics: [
				'minSlabWeightInTon',
				'maxSlabWeightInTon'
			]
		},
		{
			title: 'product.title.section.coilWeight',
			characteristics: ['minCoilWeightInPercent']
		}
	]
};

const GROUP_USE: LayoutGroupContent = {
	title: 'product.title.group.use',
	sections: [
		{
			characteristics: [
				'customerTechnicalSpecification',
				'endUserEngineeringStandard',
				'use',
				'primaryFurtherProcessing',
				'formingType',
				'productSubjectToRegulation',
				'furtherSurfaceProcessing',
				'earingRequested',
				'improvedCorrosionResistance'
			]
		},
		{
			title: 'product.title.section.customerArticle',
			characteristics: [
				'customerArticleDetailledDesignationText',
				'customerArticleStandardizedDesignation',
				'customerArticleForm',
				'customerArticleHeight',
				'customerArticleDiameter',
				'customerArticleSection',
				'customerArticleMaxDiameterAfterExpansion',
				'expansionRateCalculated',
				'customerArticleTypeOfContent',
				'customerArticleContent',
				'customerArticleVolume'
			]
		},
		{
			characteristics: [
				'customerFlangingTechnique',
				'threePiecesCanRollingDirection',
				'maxiPressureSpecificationCustomerArticle',
				'customerExpansionTechnique'
			]
		}
	]
};

const GROUP_INSULATION_RESISTANCE_TEST: LayoutGroupContent = {
	title: 'product.title.group.insulationResistanceTest',
	sections: [
		{
			characteristics: [
				'insulationResistanceTestStandardName',
				'insulationResistanceTestMinValue',
				'insulationResistanceTestUnit'
			]
		}
	]
};

const GROUP_MANAGEMENT: LayoutGroupContent = {
	title: 'product.title.group.management',
	sections: [
		{
			characteristics: ['ctsEmail']
		}
	]
};

const GROUP_PRODUCTION_PROCESS: LayoutGroupContent = {
	title: 'product.title.group.productionProcess',
	sections: [
		{
			characteristics: [
				'endTonguesInMeter',
				'staggeringCoiling',
				'coilCoresType',
				'scarfing'
			]
		}
	]
};

const GROUP_SURFACE: LayoutGroupContent = {
	title: 'product.title.group.surface',
	sections: [
		{
			characteristics: [
				'surfaceAspect',
				'betterSidePosition',
				'roughness' // also in freeData
			]
		}
	]
};

const GROUP_SURFACE_TREATMENT: LayoutGroupContent = {
	title: 'product.title.group.surfaceTreatment',
	sections: [
		{
			characteristics: [
				'oilQuantity',
				'oilingRefType',
				'chemicalTreatment'
			]
		},
		{
			title: 'product.title.section.top',
			characteristics: [
				'varnishTop',
				'primerTop',
				'thinOCTop',
				'paintThicknessRangeTopSide'
			]
		},
		{
			title: 'product.title.section.bottom',
			characteristics: [
				'varnishBottom',
				'primerBottom',
				'thinOCBottom',
				'paintThicknessRangeBottomSide'
			]
		}
	]
};

const GROUP_WELDS: LayoutGroupContent = {
	title: 'product.title.group.welds',
	sections: [
		{
			characteristics: ['totalMaxWeldNumberAllowed']
		},
		{
			characteristics: [
				'pickledWelds',
				'maxWeldNumberBeforeRolling',
				'weldIdentificationTypeCodeBeforeRolling'
			]
		},
		{
			characteristics: [
				'unrolledWelds',
				'maxWeldNumberAfterRolling',
				'weldIdentificationTypeCodeAfterRolling'
			]
		},
		{
			characteristics: [
				'laserWelds',
				'maxWeldLaserNumber',
				'weldLaserIdentificationTypeCode'
			]
		},
		{
			characteristics: [
				'interruptionAllowance',
				'interruptionIdentificationTypeCode',
				'maxInterruptionNumber'
			]
		}
	]
};

const GROUP_CERTIFICATE: LayoutGroupContent = {
	title: 'product.title.group.certificate',
	sections: [
		{
			characteristics: [
				'millTestCertificate',
				'additionalTest', // additionalTest, additionalTestFrequency
				'addTechnicalPrescription', // addTechnicalPrescription
				'logoCE',
				'certificateSteelGrade1',
				'certificateSteelGrade2',
				'certificateSteelGrade3',
				'certificateSteelGrade4',
				'certificateSteelGrade5',
				'certificateSteelGrade6',
				'certificateSteelGradeDOP'
			]
		}
	]
};

const GROUP_STAMPING: LayoutGroupContent = {
	title: 'product.title.group.stamping',
	sections: [
		{
			characteristics: [
				'markingRequirement',
				'markingSide',
				'markingPosition',
				'markingMode',
				'markingFrequency',
				'markingContent', // markingContentCode
				'markingContentFreeText'
			]
		},
		{
			characteristics: [
				'paintStampingRequirementTop',
				'paintStampingPosition',
				'paintStampingContent', // paintStampingContent
				'paintStampingContentFreeText'
			]
		}
	]
};

const GROUP_PACKING: LayoutGroupContent = {
	title: 'product.title.group.packing',
	sections: [
		{
			characteristics: [
				'packagingWithWrapping',
				'transportationConstraints',
				'unwindingDirection',
				'axisOrientationCode',
				'typeOfPallet',
				'useOfWood',
				'packagingSeaworthly',
				'contInsideSleeveCoils',
				'treatmentOfWood',
				'packagingCode'
			]
		}
	]
};

const GROUP_LABELLING: LayoutGroupContent = {
	title: 'product.title.group.labelling',
	sections: [
		{
			characteristics: [
				'qualityTracking',
				'type',
				'language',
				'labelContent', // content
				'labelLocalisation' // fixingMode, place, number
			]
		}
	]
};

export const GROUPS_LEFT = [
	GROUP_SPECIFICATIONS,
	GROUP_DIMENSIONS,
	GROUP_COATING,
	GROUP_WEIGHT_DIAMETER,
	GROUP_USE
];

export const GROUPS_LEFT_LEFT = [
	GROUP_INSULATION_RESISTANCE_TEST,
	GROUP_MANAGEMENT
];

export const GROUPS_LEFT_RIGHT = [GROUP_PRODUCTION_PROCESS];

export const GROUPS_RIGHT = [
	GROUP_SURFACE,
	GROUP_SURFACE_TREATMENT,
	GROUP_WELDS,
	GROUP_CERTIFICATE,
	GROUP_STAMPING,
	GROUP_PACKING,
	GROUP_LABELLING
];
